// Paces a streamed turn onto the page at a steady reading speed. Models send text
// in bursts (a few words, a pause, then a paragraph) and much faster than anyone
// reads, so drawing each chunk as it lands reads as jerky. The pacer buffers a
// moment of text first, then writes it at a constant speed (WORDS_PER_MINUTE at
// most, never faster to catch up). When the stream runs low it eases down, and
// when more arrives it eases back up: speed only ever changes smoothly.
//
// It paces what the reader sees: `measure(text, ended)` turns the raw stream into
// the visible text (the app strips the VERDICT / ENDORSE / CONFIDENCE lines), so
// hidden text takes no time.

export const WORDS_PER_MINUTE = 200;
const CHARS_PER_WORD = 6; // an average English word with the space after it
export const CHARS_PER_SECOND = (WORDS_PER_MINUTE * CHARS_PER_WORD) / 60; // 20

const LEAD_SECONDS = 1.2; // text buffered before writing starts...
const LEAD_WAIT_MS = 700; // ...or this long after the first text, whichever comes first
const LOW_WATER_SECONDS = 2; // with less than this buffered, ease down toward it
const EASE_SECONDS = 0.35; // how quickly the speed moves toward its target
const MAX_FRAME_SECONDS = 0.1; // a long gap between frames counts as this much

// The speed to aim for with `ahead` characters buffered: full speed while there's
// plenty, slowing in proportion as the buffer runs out (to a standstill at the end
// of what has arrived), and full speed again to the end once the stream is over.
export function targetSpeed(ahead, ended, rate = CHARS_PER_SECOND) {
  if (ended) return rate;
  return rate * Math.min(1, Math.max(0, ahead / (rate * LOW_WATER_SECONDS)));
}

// One frame's step of the speed toward `target`: an exponential ease, so the speed
// never jumps however the target moves.
export function nextSpeed(speed, target, dt) {
  return speed + (target - speed) * (1 - Math.exp(-dt / EASE_SECONDS));
}

// Where to cut `text` after revealing `n` more characters past `shown`, moved
// forward one so an emoji (a surrogate pair) is never shown half drawn.
export function cutAt(text, shown, n) {
  let end = Math.min(text.length, shown + n);
  const c = text.charCodeAt(end - 1);
  if (end < text.length && c >= 0xd800 && c <= 0xdbff) end++;
  return end;
}

// `render(visible)` draws what is revealed so far. `push` adds streamed text;
// `drain` says the stream is over and resolves once all of it is shown; `skip`
// shows everything that has arrived at once (the reader tapped the message);
// `stop` cancels without drawing more. In a background tab, which gets no animation
// frames, `drain` shows the rest at once, so a brawl never stalls on it.
//
// `rate` is characters a second, or a function giving it, so a change of speed applies
// to turns already being written (eased in, like any other change of speed).
export function createPacer(
  render,
  {
    instant = false,
    hidden = () => false,
    measure = (text) => text,
    rate = CHARS_PER_SECOND,
    now = () => performance.now(),
    schedule = (f) => requestAnimationFrame(f),
    cancel = (h) => cancelAnimationFrame(h),
  } = {},
) {
  let full = "";
  let ended = false;
  let started = false;
  let firstAt = null; // when the first text arrived
  let pos = 0; // characters revealed, fractional
  let speed = 0; // characters a second
  let drawn = -1;
  let last = null; // the previous frame's time
  let handle = 0;
  let done = null;
  let skipped = false; // the reader asked for it all: no more pacing
  const timers = [];
  const rateOf = typeof rate === "function" ? rate : () => rate;

  const visible = () => measure(full, ended);
  const draw = (text, upTo) => {
    const end = cutAt(text, 0, upTo);
    if (end === drawn) return;
    drawn = end;
    render(text.slice(0, end));
  };
  const settle = () => {
    for (const t of timers) clearTimeout(t), clearInterval(t);
    timers.length = 0;
    if (done) done();
    done = null;
  };
  const showAll = () => {
    if (handle) cancel(handle);
    handle = 0;
    const text = visible();
    pos = text.length;
    drawn = -1;
    if (text) draw(text, text.length);
    if (ended) settle();
  };
  const loop = () => {
    if (!handle) handle = schedule(tick);
  };
  const tick = () => {
    handle = 0;
    const t = now();
    const dt = last == null ? 1 / 60 : Math.min(MAX_FRAME_SECONDS, Math.max(0, (t - last) / 1000));
    last = t;
    const text = visible();
    const r = rateOf();
    if (!started) {
      started = ended || text.length >= r * LEAD_SECONDS || (firstAt != null && t - firstAt >= LEAD_WAIT_MS);
      if (!started) return loop();
    }
    pos = Math.min(pos, text.length); // the visible text can shrink as a tag line is recognised
    speed = nextSpeed(speed, targetSpeed(text.length - pos, ended, r), dt);
    pos = Math.min(text.length, pos + speed * dt);
    draw(text, Math.floor(pos));
    if (ended && pos >= text.length) {
      draw(text, text.length);
      return settle();
    }
    loop();
  };

  return {
    push(text) {
      if (!text) return;
      full += text;
      if (firstAt == null) firstAt = now();
      if (instant || skipped) showAll();
      else loop();
    },
    drain() {
      ended = true;
      if (instant || hidden()) {
        showAll();
        return Promise.resolve();
      }
      if (pos >= visible().length) {
        showAll();
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        done = resolve;
        loop();
        // A tab sent to the background stops getting frames: show the rest at once.
        timers.push(setInterval(() => hidden() && showAll(), 500));
        // And if frames stop for any other reason, don't wait forever.
        timers.push(setTimeout(showAll, ((visible().length - pos) / rateOf()) * 2000 + 5000));
      });
    },
    skip() {
      skipped = true;
      showAll();
    },
    stop() {
      if (handle) cancel(handle);
      handle = 0;
      settle();
    },
  };
}
